import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Input, Select } from 'antd';
import { useTranslation, type UseTranslationResult } from '@govard/i18n-core/react';
import {
  EntityRelatedList,
  type EntityRelatedRow,
} from '../../../components/entity-detail/EntityRelatedList.js';
import { EntityStatusChip } from '../../../components/entity-detail/EntityStatusChip.js';
import { getEnumLabel } from '../../../shared/format/enum-labels.js';
import type { ContactItem } from '../../contacts/api/contacts.client.js';

export function companyContactRow(
  contact: ContactItem,
  t: UseTranslationResult['t'],
): EntityRelatedRow {
  const meta = [
    contact.email,
    contact.phone,
    contact.source ? getEnumLabel('contactSource', contact.source, t) : null,
  ].filter(Boolean);
  return {
    id: contact.id,
    title: contact.fullName,
    href: `/contacts/${contact.id}`,
    meta: meta.length ? meta.join(' · ') : null,
    // Счётчик ведёт на вкладку «Сделки» карточки контакта.
    value: (
      <Link
        className="company-row-chip-link"
        to={`/contacts/${contact.id}?tab=deals`}
        data-testid={`company-contact-deals-${contact.id}`}
      >
        <EntityStatusChip tone={contact.dealsCount > 0 ? 'processing' : 'default'}>
          {t('crm.features.company_detail.deals_count', { count: contact.dealsCount })}
        </EntityStatusChip>
      </Link>
    ),
  };
}

export function CompanyContactsTab({
  contacts,
  loading,
  error,
}: {
  contacts: ContactItem[];
  loading: boolean;
  error: Error | null;
}): ReactElement {
  const { t, locale } = useTranslation();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('name');
  const [direction, setDirection] = useState<'ascend' | 'descend'>('ascend');
  const needle = search.trim().toLocaleLowerCase();
  const rows = contacts
    .filter((contact) => !needle || contact.fullName.toLocaleLowerCase().includes(needle))
    .sort((a, b) => {
      if (sort === 'original') return 0;
      const order =
        (sort === 'deals' ? a.dealsCount - b.dealsCount : 0) ||
        a.fullName.localeCompare(b.fullName, locale);
      return direction === 'ascend' ? order : -order;
    })
    .map((contact) => companyContactRow(contact, t));
  return (
    <EntityRelatedList
      className="contact-deals-list"
      loading={loading}
      error={error}
      emptyText={t(
        'crm.features.features.companies.pages.companydetailpage.к_компании_пока_не_привязан_ни_один_контак',
      )}
      filter={
        contacts.length ? (
          <div className="contact-deals-filter company-detail-related-sort">
            <Input.Search
              aria-label={t('crm.features.company_detail.search_contacts')}
              placeholder={t('crm.features.company_detail.search_contacts')}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Select
              size="small"
              aria-label={t('crm.entity_detail.sort')}
              value={sort}
              onChange={(value) => {
                setSort(value);
                setDirection(value === 'deals' ? 'descend' : 'ascend');
              }}
              style={{ minWidth: 150 }}
              options={[
                { value: 'name', label: t('crm.features.company_detail.sort_name') },
                { value: 'deals', label: t('crm.features.company_detail.sort_deals') },
                { value: 'original', label: t('crm.features.company_detail.sort_original') },
              ]}
            />
            <Select
              size="small"
              aria-label={t('crm.features.company_detail.sort_direction')}
              value={direction}
              onChange={setDirection}
              disabled={sort === 'original'}
              style={{ minWidth: 160 }}
              options={['ascend', 'descend'].map((value) => ({
                value,
                label: t(`crm.features.company_detail.sort_${value}`),
              }))}
            />
          </div>
        ) : null
      }
      rows={rows}
    />
  );
}
